/**
 * start-direct-download.ts — deliberately simple client helper.
 *
 * The download workflow now treats the stored URL as the source of truth.
 * It may be:
 *   - a protected /safelink URL generated by the dashboard,
 *   - a MediaFire / Google Drive / Dropbox landing page,
 *   - or a real direct file URL.
 *
 * We do NOT scrape, proxy, blob-fetch, or force direct-download extraction here.
 * Those approaches are fragile on hosting providers and mobile browsers. A native
 * anchor click is the most reliable option: it either starts the file download or
 * opens the provider/protected page in a new tab where the user can continue.
 */

export type StartResult = { ok: true } | { ok: false; error: string };

export async function startDirectDownload(
  shareUrl: string,
  filename?: string,
): Promise<StartResult> {
  const url = shareUrl.trim();
  if (!url) return { ok: false, error: "رابط التحميل فارغ." };

  const opened = triggerBrowserDownload(url, filename);
  return opened
    ? { ok: true }
    : { ok: false, error: "تعذّر فتح الرابط. انسخ الرابط وافتحه يدوياً." };
}

/**
 * Kick off the browser's native downloader via a hidden <a download> click.
 * Works on iOS Safari, Android Chrome, and desktop.
 *
 * target=_blank is a safety net: if the CDN returns an inline HTML error
 * page instead of the file, it opens in a new tab rather than navigating
 * the app away.
 */
function triggerBrowserDownload(url: string, filename?: string): boolean {
  try {
    const a = document.createElement("a");
    a.href = url;
    if (filename && isLikelyDirectFile(url)) a.download = filename;
    a.rel = "noopener";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return true;
  } catch {
    return false;
  }
}

function isLikelyDirectFile(url: string): boolean {
  return /\.(mp4|mkv|webm|avi|mov|zip|rar|7z|pdf)(\?|#|$)/i.test(url);
}
